import { useState } from 'react'
import type { TenantGroupCodesSelectProps } from './TenantGroupCodesSelect.types'
import './TenantGroupCodesSelect.css'
import { compareSelectLabels } from '../../helpers/compareSelectLabels'

// Strict multi-picker for tenant-group codes: the operator can only select from codes
// that actually exist. A filter box narrows a scrollable checkbox list, and selected
// codes appear as removable chips above it.
export function TenantGroupCodesSelect({
  availableCodes,
  selected,
  onChange,
  isLoading = false,
}: TenantGroupCodesSelectProps) {
  const [filter, setFilter] = useState('')

  const sortedCodes = [...availableCodes].sort(compareSelectLabels)
  const normalizedFilter = filter.trim().toLowerCase()
  const visibleCodes = normalizedFilter
    ? sortedCodes.filter((code) =>
        code.toLowerCase().includes(normalizedFilter)
      )
    : sortedCodes

  function toggle(code: string) {
    if (selected.includes(code)) {
      onChange(selected.filter((c) => c !== code))
      return
    }
    onChange([...selected, code])
  }

  if (isLoading) {
    return (
      <p className="tenant-group-codes-select-empty">Loading tenant groups…</p>
    )
  }

  if (availableCodes.length === 0) {
    return (
      <p className="tenant-group-codes-select-empty">
        No tenant groups available.
      </p>
    )
  }

  return (
    <div className="tenant-group-codes-select w-100">
      {selected.length > 0 && (
        <ul className="tenant-group-codes-select-chips">
          {selected.map((code) => (
            <li key={code} className="tenant-group-codes-select-chip">
              <span>{code}</span>
              <button
                type="button"
                className="tenant-group-codes-select-chip-remove"
                aria-label={`Remove ${code}`}
                onClick={() => toggle(code)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        type="text"
        className="form-control form-control-sm"
        placeholder="Filter tenant groups…"
        aria-label="Filter tenant groups"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />

      <ul className="tenant-group-codes-select-list">
        {visibleCodes.length === 0 && (
          <li className="tenant-group-codes-select-empty">No matches.</li>
        )}
        {visibleCodes.map((code) => {
          const isSelected = selected.includes(code)
          return (
            <li key={code}>
              <label className="tenant-group-codes-select-option">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={isSelected}
                  onChange={() => toggle(code)}
                />
                <span>{code}</span>
              </label>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
